/**
 * Entry application component used to compose providers and render Routes.
 * */

import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import AppProvider from 'src/components/App/AppProvider';

import { ErrorFallbackView } from '@components/ErrorView';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/react';
import { navigationConfirmation } from '@utils/navigationConfirmation';

import { I18nProvider } from '../_metronic/i18n';
import { LayoutSplashScreen, MaterialThemeProvider } from '../_metronic/layout';

import { Routes } from './Routes';

export default function App({ store, basename }) {
  return (
    /* Provide Redux store */
    <I18nProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AppProvider reduxStore={store}>
          {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
          <React.Suspense fallback={<LayoutSplashScreen />}>
            {/* Override `basename` (e.g: `homepage` in `package.json`) */}
            <BrowserRouter basename={basename} getUserConfirmation={navigationConfirmation}>
              {/* This library only returns the location that has been active before the recent location change in the current window lifetime. */}
              <MaterialThemeProvider>
                {/* Provide `react-intl` context synchronized with Redux state.  */}
                <ToastContainer hideProgressBar pauseOnHover />
                {/* Top-level safety net: catches render crashes in the app shell
                    (sidebar, header, providers) and any failing inner fallback. */}
                <Sentry.ErrorBoundary fallback={<ErrorFallbackView />}>
                  {/* Render routes with provided `Layout`. */}
                  <Routes />
                </Sentry.ErrorBoundary>
              </MaterialThemeProvider>
            </BrowserRouter>
          </React.Suspense>
        </AppProvider>
      </LocalizationProvider>
    </I18nProvider>
  );
}
